import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Tabs,
  Tab
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Computer as ComputerIcon,
  Settings as SettingsIcon,
  Schedule as ScheduleIcon,
  Code as CodeIcon,
  Update as UpdateIcon,
  CalendarToday as CalendarIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  DeleteSweep as DeleteSweepIcon,
  Sync as SyncIcon,
  Email as EmailIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import AccountCatalogSyncSettingsTab from './AccountCatalogSyncSettingsTab';
import { useDatabaseHealthService } from '../../services/databaseHealthService';
import { frontendHealthService } from '../../services/frontendHealthService';
import { useAuth } from '../../context/AuthContext';
import type { DatabaseHealthResponse } from '../../types/databaseHealth';
import type { FrontendHealthResponse } from '../../types/frontendHealth';
import AuditLogPurgeSettingsTab from './AuditLogPurgeSettingsTab';
import SpecialistCaseMailCcSettingsTab from './SpecialistCaseMailCcSettingsTab';
import LoggedInUsersSettingsTab from './LoggedInUsersSettingsTab';

const SettingsPage: React.FC = () => {
  const { jwtToken } = useAuth();
  const { checkDatabaseHealth } = useDatabaseHealthService();
  const [tab, setTab] = useState(0);
  const [databaseHealth, setDatabaseHealth] = useState<DatabaseHealthResponse | null>(null);
  const [frontendHealth, setFrontendHealth] = useState<FrontendHealthResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [frontendLoading, setFrontendLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [frontendError, setFrontendError] = useState<string | null>(null);

  const fetchDatabaseHealth = async () => {
    if (!jwtToken) {
      setError('No hay token de autenticación');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await checkDatabaseHealth();

      if (response.ok) {
        setDatabaseHealth(response.data);
      } else {
        setError(response.error || 'Error al verificar la salud de la base de datos');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const fetchFrontendHealth = async () => {
    setFrontendLoading(true);
    setFrontendError(null);

    try {
      const response = await frontendHealthService.getFrontendHealth();

      if (response.ok) {
        setFrontendHealth(response.data);
      } else {
        setFrontendError(response.error || 'Error al verificar la salud del frontend');
      }
    } catch (err) {
      setFrontendError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setFrontendLoading(false);
    }
  };

  useEffect(() => {
    fetchDatabaseHealth();
    fetchFrontendHealth();
  }, []);

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    return status === 'UP' ? 'success' : 'error';
  };

  const getStatusIcon = (status: string) => {
    return status === 'UP' ? <CheckCircleIcon /> : <ErrorIcon />;
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
        <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Configuración del Sistema
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab icon={<HealthAndSafetyIcon />} iconPosition="start" label="Salud del sistema" />
        <Tab icon={<DeleteSweepIcon />} iconPosition="start" label="Purga de auditoría" />
        <Tab icon={<SyncIcon />} iconPosition="start" label="Sync cuentas" />
        <Tab icon={<EmailIcon />} iconPosition="start" label="Correos canalización" />
        <Tab icon={<PeopleIcon />} iconPosition="start" label="Usuarios conectados" />
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" component="h2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <StorageIcon sx={{ mr: 1 }} />
                    Estado de la Base de Datos
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                    onClick={fetchDatabaseHealth}
                    disabled={loading}
                  >
                    Actualizar
                  </Button>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {loading && !databaseHealth && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                )}

                {databaseHealth && (
                  <Stack spacing={2}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6">Estado General</Typography>
                        <Chip
                          icon={getStatusIcon(databaseHealth.status)}
                          label={databaseHealth.status}
                          color={getStatusColor(databaseHealth.status)}
                          variant="filled"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {databaseHealth.message}
                      </Typography>
                    </Paper>

                    <Divider />

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <ComputerIcon sx={{ mr: 1, fontSize: 20 }} />
                            Servidor
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {databaseHealth.server_name}
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <StorageIcon sx={{ mr: 1, fontSize: 20 }} />
                            Base de Datos
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {databaseHealth.database_name}
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <SettingsIcon sx={{ mr: 1, fontSize: 20 }} />
                            Perfil Activo
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {databaseHealth.active_profile}
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <ScheduleIcon sx={{ mr: 1, fontSize: 20 }} />
                            Última Verificación
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {formatTimestamp(databaseHealth.timestamp)}
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CodeIcon sx={{ mr: 1, fontSize: 20 }} />
                            Versión de la Aplicación
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {databaseHealth.app_version}
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CalendarIcon sx={{ mr: 1, fontSize: 20 }} />
                            Último Cambio
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {databaseHealth.last_change_date}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Configuración Esperada
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Base de datos configurada: {databaseHealth.configured_database}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip
                          label={databaseHealth.configuration_match ? 'Configuración Correcta' : 'Configuración Incorrecta'}
                          color={databaseHealth.configuration_match ? 'success' : 'warning'}
                          size="small"
                        />
                      </Box>
                    </Paper>

                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <UpdateIcon sx={{ mr: 1, fontSize: 20 }} />
                        Cambios Recientes
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {databaseHealth.recent_changes}
                      </Typography>
                    </Paper>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" component="h2" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CodeIcon sx={{ mr: 1 }} />
                    Estado del Frontend
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={frontendLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                    onClick={fetchFrontendHealth}
                    disabled={frontendLoading}
                  >
                    Actualizar
                  </Button>
                </Box>

                {frontendError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {frontendError}
                  </Alert>
                )}

                {frontendLoading && !frontendHealth && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                )}

                {frontendHealth && (
                  <Stack spacing={2}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6">Estado General</Typography>
                        <Chip
                          icon={getStatusIcon(frontendHealth.status)}
                          label={frontendHealth.status}
                          color={getStatusColor(frontendHealth.status)}
                          variant="filled"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {frontendHealth.message}
                      </Typography>
                    </Paper>

                    <Divider />

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CodeIcon sx={{ mr: 1, fontSize: 20 }} />
                            Versión Actual
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {frontendHealth.version}
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CalendarIcon sx={{ mr: 1, fontSize: 20 }} />
                            Último Despliegue
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {frontendHealth.lastDeployment}
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <SettingsIcon sx={{ mr: 1, fontSize: 20 }} />
                            Entorno
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {frontendHealth.environment}
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <ScheduleIcon sx={{ mr: 1, fontSize: 20 }} />
                            Fecha de Build
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {formatTimestamp(frontendHealth.buildDate)}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <UpdateIcon sx={{ mr: 1, fontSize: 20 }} />
                        Cambios Recientes Desplegados
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {frontendHealth.recentChanges}
                      </Typography>
                    </Paper>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tab === 1 && <AuditLogPurgeSettingsTab />}

      {tab === 2 && <AccountCatalogSyncSettingsTab />}

      {tab === 3 && <SpecialistCaseMailCcSettingsTab />}

      {tab === 4 && <LoggedInUsersSettingsTab />}
    </Box>
  );
};

export default SettingsPage;
